<!-- Localized -->
<template>
  <div class="app-settings flex items-start gap-5">
    <Block
      :title="$t('pages.theme_admin.theme_admin')"
      class="w-80 max-h-full"
      contentClass="flex flex-col gap-3 overflow-auto"
    >
      <!-- Logo -->
       <label class="flex items-center gap-2 text-base-content font-semibold cursor-pointer">
        {{ $t('pages.theme_admin.logo') }}
       </label>
      <div class="settings-block p-4 rounded-lg bg-gray-100" :class="[!checkPermission('theme.edit') && 'opacity-50 pointer-events-none cursor-not-allowed' ]" >
        <ImageUploader
          type="logo"
          :tenant_id="getTenantId"
          :client_id="getClientId"
          :is_client="getIsClient"
          :value="themeLogo"
          @setLoader="setLoader"
          @input="saveUploadedImage"
          @removeLogo="removeUploadedImage"
          :settings="{
            disabled:!checkPermission('theme.edit')
          }"
        />
      </div>
      <!-- FavIcon -->
      <label class="flex items-center gap-2 text-base-content font-semibold cursor-pointer">
       {{ $t('pages.theme_admin.favicon_label') }}
      </label>
      <div class="settings-block p-4 rounded-lg bg-gray-100" :class="[!checkPermission('theme.edit') && 'opacity-50 pointer-events-none cursor-not-allowed' ]" >
      <ImageUploader
        type="favicon"
        :tenant_id="getTenantId"
        :client_id="getClientId"
        :is_client="getIsClient"
        :value="themeFavicon"
        @setLoader="setLoader"
        @input="saveUploadedImage"
        @removeLogo="removeUploadedImage"
        :settings="{
          disabled:!checkPermission('theme.edit')
        }"
      />
      </div>
      <label class="useDefault flex items-center gap-2">
        <Checkbox
          :disabled="!checkPermission('theme.edit')"
          class="useDefault__checkbox"
          v-model="useDefault"
          @input="setUseDefault"
        />
        <span class="text-base-content text-xs font-semibold cursor-pointer">
          {{ $t('pages.theme_admin.use_default_system_settings') }}
        </span>
      </label>
      <div
        class="settings-block p-4 rounded-lg bg-gray-100 flex flex-col gap-3 overflow-auto scroll-bar"
        :class="{'cursor-not-allowed': useDefault}"
      >
        <ColorPicker
          v-for="(color, key) in colors"
          :key="key"
          :class="{'opacity-70': useDefault}"
          :name="color.name"
          :value="color.value"
          @toggle="handleColor(color)"
          :isOpen="activeColor === color.var"
          :disabled="useDefault || !checkPermission('theme.edit')"
          @change="applyColor(key, $event)"
        />
      </div>

      <template v-if="!getIsClient">
        <div class="flex mb-2 mt-4">
          <label class="useDefault flex items-center gap-2">
            <Checkbox
              class="useDefault__checkbox"
              v-model="usePrivacyPolicy"
            />
            <span class="text-base-content text-xs font-semibold cursor-pointer">
              {{ $t('pages.theme_admin.use_footer') }}
            </span>
          </label>
        </div>

        <div class="flex flex-col bg-gray-100 rounded-xl p-4 w-full h-48 justify-center items-center">
          <div class="w-full mb-2 dropdown" :class="{ 'limit-reached': isFooterFieldLimitReached }">
            <FormulateInput
              v-model="footerField"
              placeholder="select"
              :element-class="
                (context, classes) => ['flex-1 min-w-full  border border-8', {'bg-white': usePrivacyPolicy}].concat(classes)
              "
              v-bind="multiselectConfig"
            />
          </div>

          <div
            ref="footerTextarea"
            :style="{
              ...textFieldStyles,
              'background-color': !usePrivacyPolicy ? '#eaeaea' : 'white'
            }"
            class="rounded-xl px-4 py-2 border-2 overflow-y-auto"
            :class="[ {'opacity-50' : !usePrivacyPolicy}]"
            :contenteditable="usePrivacyPolicy"
            @paste="($event) => handlePaste($event, 'footer')"
            @input="($event) => handleInput($event, 'footer')"
            data-placeholder="Enter your text here"
          />
          <div
            class="flex items-start text-left text-sm text-red-600 w-full p-1" 
            v-if="errorInPrivacyPolicy"
          >
            {{ $t(`pages.theme_admin.${errorInPrivacyPolicy}`)}}
          </div>
        </div>

        <div class="flex mb-2 mt-4">
          <label class="useDefault flex items-center gap-2">
            <Checkbox
              class="useDefault__checkbox"
              v-model="privacyStatement.active"
            />
            <span class="text-base-content text-xs font-semibold cursor-pointer">
              {{ $t('pages.theme_admin.privacy_statement') }}
            </span>
          </label>
        </div>

        <div class="flex flex-col bg-gray-100 rounded-xl p-4 w-full h-48  justify-center items-center">
          <div class="w-full mb-2 dropdown">
            <Dropdown
              :placeholder="$t('pages.theme_admin.select_privacy_statement')"
              v-model="privacyStatement.field"
              v-bind="singleSelectConfig"
            />
          </div>

          <div
            ref="privacyStatementTextarea"
            :style="{
              ...textFieldStyles,
              'background-color': !privacyStatement.active ? '#eaeaea' : 'white'
            }"
            class="rounded-xl px-4 py-2 border-2 overflow-y-auto"
            :class="[ {'opacity-50' : !privacyStatement.active}]"
            :contenteditable="privacyStatement.active"
            :data-placeholder="$t('pages.theme_admin.agreement_text')"
            @paste="($event) => handlePaste($event, 'privacyStatement')"
            @input="($event) => handleInput($event, 'privacyStatement')"
          />
          <div
            class="flex items-start text-left text-sm text-red-600 w-full p-1" 
            v-if="privacyStatement.error"
          >
            {{ $t(`pages.theme_admin.${privacyStatement.error}`)}}
          </div>
        </div>
      </template>

      <div class="flex justify-between gap-1">
        <Button
          class="w-24"
          :text="$t('actions.cancel')"
          @click="cancelColors"
          :disabled="!checkPermission('theme.edit') || loading"
        />
        <Button
          class="w-24 bg-blue-500 border-blue-500 focus:bg-blue-600 active:bg-blue-600  hover:bg-blue-600 hover:border-blue-600"
          type="secondary"
          :text="$t('actions.save')"
          :disabled="!checkPermission('theme.edit') || loading"
          @click="saveColors"
        />
      </div>
    </Block>
    <Block
      class="flex-1 self-stretch"
      contentClass="flex items-center justify-center text-gray-500"
    >
      <Preview /> 
    </Block>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Block from '@shared/components/block-container';
import ColorPicker from '@/components/color-picker';
import Checkbox from "@/components/checkbox";
import Button from "@/components/button";
import ImageUploader from './components/ImageUploader';
import Dropdown from "@shared/components/dropdown-base";
import ContentMixin from "./mixins/content";
import { checkPermission } from "@shared/utils/functions"
import Preview from "./components/preview"
import { getColorValues } from "@/modules/theme/utils"
import axios from "@/axios"

/* 
  Color sets:
    - getDefaultColors - hardcoded default colors.
      * For the client user use tenant colors as default.
    - getTenantConfig.uiconfig.color_config - colors from server.
    - getThemeColors - Colors applied on the frontend.
  Store: tenant-app/src/modules/theme/store.js

  On init:
    - reset_flag:
      - true:
        - 'Use default system settings' = true.
        - Parse colors from server.
        - Use these colors as default.
      - false:
        - 'Use default system settings' = false.
        - Parse hardcoded colors.
        - These colors are default.

  Actions:
    - 'Apply' color - set new color value in FRONTEND config (not default).
    - 'Cancel' color - reset color to its applied on the frontend value.
    - 'Save' - send all applied colors & logo to the API; reset colors to default values if useDefault.
    - 'Cancel' - reset all colors & logo to saved (server) values.
*/

// TODO Don't use default colors from store - just skip colors which values hasn't been set
// TODO as their default values are already set at @shared/assets/styles/style.scss

export default {
  name: 'theme-admin',

  mixins: [ContentMixin],

  components: {
    Block,
    ColorPicker,
    Checkbox,
    Button,
    Preview,
    ImageUploader,
    Dropdown,
  },

  data() {
    return {
      useDefault: false,
      colors: null,
      logo: '',
      activeColor: '',
      loading: false,

      // TODO: Rework footer model to make it same as privacyStatement
      usePrivacyPolicy: false,
      privacyPolicyText: '',
      errorInPrivacyPolicy: '',
      footerField: null,
      footerFieldOption: [],

      privacyStatement: {
        field: null,
        text: "",
        active: false,
        error: false,
      },

      loader: {
        logo: false,
        favicon: false,
      }
    }
  },

  created() {
    this.useDefault = this.getThemeUseDefault;
    this.setColorsFromFrontend();
  },

  async mounted() {
    try {
      await this.fetchFooterDetails();
      this.footerField = this.getFooterFields || null;
      this.privacyPolicyText = this.getFooterText || '';
      // if (this.footerField?.length && this.privacyPolicyText)
      this.usePrivacyPolicy = this.getUseFooter;
      
      // Set initial content
      this.$nextTick(() => {
        if (this.$refs.footerTextarea && this.privacyPolicyText) {
          this.$refs.footerTextarea.innerHTML = this.privacyPolicyText;
        }
      });

      await this.setFooterDetails();

      if (!this.getIsClient) {
        await this.fetchPrivacyStatement();
      }
    } catch(err) {
      console.log("err", err);
    }
  },

  title: "Theme admin",

  computed: {
    ...mapGetters([
      'getTenantConfig',
      'getClientConfig',
      'getThemeColors',
      'getDefaultColors',
      'getThemeLogo',
      'getThemeClientLogo',
      'getTenantId',
      'getThemeUseDefault',
      'getLoginTenantDetails',
      'getThemeFavicon',
      'getThemeClientFavicon',
      'getIsClient',
      'getClientId',
      'getThemeLogoForUploader',
      'getClientFavicon',
      'getClientLogo',
      "getFooterFields",
      "getFooterText",
      "getUseFooter",
    ]),

    themeAPI() {
      return {
        storeTheme: this.getIsClient
          ? this.setClient
          : this.setTenant,
        storeLogo: this.getIsClient
          ? this.setClientThemeLogo
          : this.setTenantThemeLogo,
        storeFavicon: this.getIsClient
          ? this.setClientThemeFavicon
          : this.setTenantThemeFavicon,
        // cache FE changes:
        setConfig: this.getIsClient
          ? this.SET_CLIENT_CONFIG
          : this.SET_TENANT_CONFIG,
        setColors: this.getIsClient
          ? this.SET_CLIENT_COLORS
          : this.SET_TENANT_COLORS,

        getCurrentConfig: this.getIsClient
          ? this.getClientConfig
          : this.getTenantConfig,
      }
    },

    themeLogo() {
      return this.getIsClient ? (this.getClientLogo || "") : this.getThemeLogo;
    },
    themeFavicon() {
      return this.getIsClient ? this.getThemeClientFavicon : this.getThemeFavicon;
    },
  },

  methods: {
    checkPermission,
    ...mapActions([
      'setTenant',
      'setTenantThemeLogo',
      'setClientThemeLogo',
      'setTenantThemeFavicon',
      'setClientThemeFavicon',
      'fetchClientUpdatedDetails',
      'setFooterText',
      'setFooterFields',
      'fetchFooterDetails',
    ]),
    ...mapActions({
      setClientMethod: 'setClient',
    }),
    ...mapMutations([
      // 'SET_THEME_COLORS',
      'SET_THEME_COLOR',
      'SET_THEME_LOGO',
      'SET_TENANT_CONFIG',
      'SET_CLIENT_CONFIG',
      'SET_TENANT_COLORS',
      'SET_CLIENT_COLORS',
      'SET_THEME_USE_DEFAULT',
      'SET_COLORS_WITH_SHADES',
    ]),

    async setClient(payload) {
      const response = await this.setClientMethod({
        ...payload,
        client_id: this.getClientId,
      });
      return response;
    },

    setLoader(payload, type) {
      this.loader[type] = payload 
      this.loading = payload;
    },
    setUseDefault(payload) {
      this.SET_THEME_USE_DEFAULT(payload);
      this.setColorsFromFrontend();
    },

    // use to populate component's local colors with values received from the server:
    setColorsFromServer() {
      const useDefault = this.useDefault;
      this.colors = Object.entries(this.themeAPI.getCurrentConfig.uiconfig.color_config)
        .reduce((result, [key, value]) => {
            result[key] = {
              ...this.getThemeColors[key],
              value: useDefault ? this.getDefaultColors[key].value : value,
            }
            return result;
        }, {});
    },
    // use to populate component's local colors with temporary frontend colors' values:
    setColorsFromFrontend() {
      const useDefault = this.useDefault;
      this.colors = Object.entries(this.getThemeColors)
        .reduce((result, [key, value]) => {
          if (key in this.themeAPI.getCurrentConfig.uiconfig.color_config) {
            result[key] = {
              ...this.getThemeColors[key],
              // if useDefault use default colors; else use color saved on the frontend if exist: 
              value: useDefault ? this.getDefaultColors[key].value : (value.value || this.getDefaultColors[key].value),
            }
          }
          return result;
        }, {});
    },
    applyColor(color, value) {
      this.colors[color].value = value;
      this.SET_THEME_COLOR({color, value});
    },
    // use to reset all applied on the frontend colors with local colors from this component:
    applyCurrentColors() {
      this.SET_COLORS_WITH_SHADES({
        colors: getColorValues(this.colors),
      });
    },

    handleColor(color) {
      console.log(color);
      this.activeColor = color.var
    },
    async updateConfig(image, type, updateType){
      try {
        const payload = {
          ...this.themeAPI.getCurrentConfig,
          ...(type === "logo" ? { logo_url: image } : {}),
          ...(type === "favicon" ? { favicon_url: image } : {}),
        };
        await this.themeAPI.storeTheme(payload);

        if(this.getIsClient) {
          await this.fetchClientUpdatedDetails();
          type === "logo"
            ? await this.setClientThemeLogo({logo: this.getClientLogo, updateType})
            : await this.setClientThemeFavicon({favicon: this.getClientFavicon, updateType});
        } else {
          type === "logo"
            ? await this.setTenantThemeLogo(image)
            : await this.setTenantThemeFavicon(image);
        }
      } catch (error) {
        console.log("error", error);
        this.$toast.error(this.$t('pages.theme_admin.failed_to_save'));
      }
    },
    showImageUploadToast(type) {
       if (type === "logo") {
         this.$toast.success(this.$t('pages.theme_admin.the_logo_has_been_uploaded_successfully'));
       } else if (type === "favicon") {
         this.$toast.success(
           this.getIsClient
             ? this.$t('pages.theme_admin.client_favicon_uploaded_successfully')
             : this.$t('pages.theme_admin.favicon_uploaded_successfully')
         );
       }
      },
    async saveUploadedImage(image, type) {
        await this.updateConfig(image, type, 'add');
        this.showImageUploadToast(type);
    },
    // async saveFavicon(icon) {
    //     await this.updateConfig(icon, "favicon");
    //     this.setThemeFavicon(icon);
    //     // this.$toast.success(this.$t('pages.theme_admin.the_logo_has_been_uploaded_successfully'));
    //     this.$toast.success("Favicon uploaded successfully");

    // },

    async saveColors() {
      try {
        // this.SET_THEME_COLORS(this.colors);

        this.cleanTextArea();

        if (this.privacyPolicyText) {
          this.validateTextArea('footer');
        }

        if (this.privacyStatement.text) {
          this.validateTextArea('privacyStatement');
        }

        if (this.errorInPrivacyPolicy || this.privacyStatement.error) {
          return;
        }

        if (this.privacyStatement.active && !this.privacyStatement.text?.length) {
          this.privacyStatement.error = 'please_select_privacy_statement';
          return;
        }

        await this.updateFooterDetails();
        await this.fetchFooterDetails();

        if (!this.getIsClient) {
          await this.savePrivacyStatement();
        }

        const { uiconfig } = this.themeAPI.getCurrentConfig;
        const { color_config } = uiconfig;
        // reset color if has been changed:
        const newColors = Object.entries(color_config).reduce((result, [key, value]) => {
          result[key] = this.getThemeColors[key].value || value;
          return result;
        }, {})
        const newConfig = {
          ...this.themeAPI.getCurrentConfig,
          logo_url: this.getIsClient ?  this.getThemeClientLogo : this.getThemeLogo,
          favicon_url:  this.getIsClient ? this.getThemeClientFavicon : this.getThemeFavicon,
          reset_flag: this.useDefault,

          uiconfig: {
            ...uiconfig,
            color_config: newColors,
          }
        }
        this.themeAPI.setConfig({...newConfig});
        // if (this.useDefault) {
        //   delete newConfig.uiconfig;
        // } 
        await this.themeAPI.storeTheme(newConfig);

        // update colors in Vuex with new values stored on the server:
        this.themeAPI.setColors(newColors);

        this.setColorsFromFrontend();
        // apply default colors' values on the frontend:
        if (this.useDefault) this.applyCurrentColors();
        this.$toast.success(this.$t('pages.theme_admin.saved_successfully'));
      } catch (error) {
        this.$toast.error(this.$t('pages.theme_admin.failed_to_save'));
      }
    },
    cancelColors() {
      console.log("call 2")
      // reset all frontend changes in config and colors with values from the server:
      this.SET_TENANT_CONFIG(this.getTenantConfig);
      this.SET_CLIENT_CONFIG(this.getClientConfig);
      this.setColorsFromServer();
    },
    async removeUploadedImage(file_logo, type) {
      try {
        let body = null;
        let url = `/tenant/${type === 'favicon' ? 'delete_': ''}${type}/${this.getTenantId}`;
        if(this.getIsClient) {
            url = `/client/${type}/${this.getClientId}`;
            body  = { data: { logo_url: file_logo }  }
        } else {
          body  = { data: type === 'logo' ? { logo_url: file_logo } : {favicon_logo_url: file_logo} }
        }
        
        await axios.delete(url, body);
      
        let configs = this.themeAPI.getCurrentConfig;

        type === 'logo' ? configs.logo_url = "" : configs.favicon_url = "";
        await this.updateConfig("", type, 'remove');
        // if(!this.getIsClient) {
        //   console.log("call 3")
        //   type === 'logo' ? this.setTenantThemeLogo("") : this.setTenantThemeFavicon("");
        //   // this.SET_TENANT_CONFIG(configs);
        // } else {
        //    type === 'logo' ? this.setClientThemeLogo("") : this.setClientThemeFavicon("");
        //     // this.SET_CLIENT_CONFIG(configs);
        // }
       
        
        // this.updateConfig("");
        type === 'logo' ? this.$toast.success(this.$t('pages.theme_admin.the_logo_has_been_removed_successfully')) : this.$toast.success(this.$t('pages.theme_admin.the_favicon_has_been_removed_successfully'));
      } catch (error) {
        if(error?.response?.data?.detail === "Forbidden" && error?.response?.status === 403) {
          let configs = this.themeAPI.getCurrentConfig;
          type === 'logo' ? configs.logo_url = "" : configs.favicon_url = "";
          await this.updateConfig("", type, 'remove');
          // if(!this.getIsClient) {
          //   console.log("call 4")
          //   // this.SET_TENANT_CONFIG(configs);
          //   type === 'logo' ? this.seTenantThemeLogo("") : this.setTenantThemeFavicon("");
          // } else {
          //   // this.SET_CLIENT_CONFIG(configs);
          //   type === 'logo' ? this.setClientThemeLogo("") : this.setClientThemeFavicon("");
          // }
        }
        console.log('error :>> ', error);
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.useDefault {
  &__checkbox {
    ::v-deep {
      .input-wrapper {
        display: flex;
      }
    }
  }
}

.dropdown.limit-reached { 
   ::v-deep {

    .multiselect__single,
    .multiselect__placeholder,
    .multiselect__option {
      white-space: normal !important;
    } 

  }
}

[contenteditable="true"]:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}

[contenteditable="true"]:focus {
  outline: none;
}

[contenteditable="false"] {
  cursor: not-allowed;
}
</style>
